import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { _className, Button, ComponentSync, LL_H_C, LL_V_L, ModuleFE_Toaster, ModuleFE_WindowMessenger, ModuleFE_XHR, SimpleListAdapter, stopPropagation, StorageKey, TS_DropDown, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { Key_AdvisorMountedMessage, Key_CloseActivity } from './types.js';
import { Default_EHRID, HeaderKey_AdvisorSessionId } from './shared.js';
import './Page_Demo.scss';
import { AssertionException, BadImplementationException, dbObjectToId, deepClone, exists, ImplementationMissingException, sortArray, } from '@nu-art/ts-common';
import { _EmptyQuery } from '@nu-art/firebase-shared';
import { ModuleFE_EncounterSimulation, ModuleFE_SimulationDemoEvent, } from '@app/ehr-emulator-frontend';
import { ICONS, ResizableWrapper } from '@app/styles';
import { Demo_MockMiddlePanel } from './Demo_MockMiddlePanel.js';
import { ModuleFE_Launch } from '../../../modules/ModuleFE_Launch/ModuleFE_Launch.js';
import { DropDown_Environment, DropDown_Facility, DropDown_Organization, ModuleFE_EHRInstallation, ModuleFE_Environment, ModuleFE_Facility, ModuleFE_Organization } from '@app/org-frontend';
export const defaultEHRId = Default_EHRID;
const storage_UserSelection = new StorageKey('advisor-launch__user-selection');
const storage_LastSelectedPatient = new StorageKey('advisor-launch__last-selected-patient');
const getiFrameId = (patientId) => `internal-iframe-${patientId}`;
export class Page_Demo extends ComponentSync {
    //######################### Class Properties #########################
    receiver;
    iframeRef = React.createRef();
    messenger_setAdvisorEHRId = () => {
        if (!this.iframeRef.current)
            return;
        return ModuleFE_WindowMessenger.createMessenger(this.iframeRef.current.contentWindow);
    };
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.organizationIds ??= [];
        state.openEncounters ??= [];
        return state;
    }
    componentDidMount() {
        this.receiver.mount();
        this.loadData();
    }
    componentWillUnmount() {
        this.receiver.unmount();
    }
    constructor(props) {
        super(props);
        this.receiver = ModuleFE_WindowMessenger
            .createReceiver(window.origin)
            // .addProcessor(Key_ExpandWidthMessage, this.onWidthExpand)
            // .addProcessor(Key_SetAdvisorWidth, this.setAdvisorWidth)
            .addProcessor(Key_AdvisorMountedMessage, this.advisorIframeMounted)
            .addProcessor(Key_CloseActivity, () => this.closeCurrentOpenedTab());
    }
    //######################### Events #########################
    advisorIframeMounted = async (message) => {
        this.logInfo('Iframe Mounted', message);
        ModuleFE_XHR.addDefaultHeader(HeaderKey_AdvisorSessionId, () => message.sessionId);
        setTimeout(() => this.forceUpdate(), 3000);
    };
    //######################### Logic #########################
    getAllowedFacilityIds = (orgId = this.state.selectedOrganization?._id) => {
        if (!orgId)
            return [];
        const facilities = ModuleFE_Facility.cache.filter(facility => facility.organizationId === orgId);
        sortArray(facilities, f => f.name);
        sortArray(facilities, f => f.name.toLowerCase().includes('quai') ? -1 : 1);
        return facilities.map(dbObjectToId);
    };
    getAllowedEnvIds = (orgId = this.state.selectedOrganization?._id) => {
        if (!orgId)
            return [];
        const allowedInstallationIds = ModuleFE_EHRInstallation.cache
            .filter(ehr => ehr.organizationId === orgId)
            .map(dbObjectToId);
        return ModuleFE_Environment.cache
            .filter(env => allowedInstallationIds.includes(env.ehrInstallationId))
            .map(dbObjectToId);
    };
    getAllowedPatients = (environment) => {
        if (!environment)
            return [];
        let patients = environment.demoPatients ?? [];
        const launchedPatientIds = this.state.openEncounters.map(encounter => encounter.patientId);
        return patients.filter(patient => {
            const patientId = ('fhirPatient' in patient) ? patient.fhirPatient : patient.patientId;
            return !launchedPatientIds.includes(patientId);
        });
    };
    loadData = async () => {
        try {
            const demoEvents = await ModuleFE_SimulationDemoEvent.v1.query(_EmptyQuery).executeSync();
            const allowedOrganizationIds = (await ModuleFE_Organization._v1.getAllowedOrganizationIds({}).executeSync()).organizationIds;
            const userSelection = storage_UserSelection.get();
            const lastSelectedPatient = storage_LastSelectedPatient.get();
            const lastSelectedPatientId = exists(lastSelectedPatient)
                ? (('fhirPatient' in lastSelectedPatient) ? lastSelectedPatient.fhirPatient : lastSelectedPatient.patientId)
                : undefined;
            //Infer selected organization
            const selectedOrganization = (exists(userSelection?.orgId) && allowedOrganizationIds.includes(userSelection.orgId))
                ? ModuleFE_Organization.cache.unique(userSelection.orgId)
                : ModuleFE_Organization.cache.unique(allowedOrganizationIds[0]);
            //Infer selected facility
            const allowedFacilityIds = this.getAllowedFacilityIds(selectedOrganization?._id);
            const selectedFacility = (exists(userSelection?.facilityId) && allowedFacilityIds.includes(userSelection.facilityId))
                ? ModuleFE_Facility.cache.unique(userSelection.facilityId)
                : ModuleFE_Facility.cache.unique(allowedFacilityIds[0]);
            //Infer selected environment
            const allowedEnvIds = this.getAllowedEnvIds(selectedOrganization?._id);
            const selectedEnvironment = (exists(userSelection?.envId) && allowedEnvIds.includes(userSelection.envId))
                ? ModuleFE_Environment.cache.unique(userSelection?.envId)
                : ModuleFE_Environment.cache.unique(allowedEnvIds[0]);
            //Infer selected patient
            const allowedPatients = this.getAllowedPatients(selectedEnvironment);
            const selectedPatient = (exists(lastSelectedPatientId) && allowedPatients.find(i => {
                const patientId = ('fhirPatient' in i) ? i.fhirPatient : i.patientId;
                return patientId === lastSelectedPatientId;
            })) ? lastSelectedPatient : allowedPatients[0];
            storage_UserSelection.set({
                orgId: selectedOrganization?._id,
                facilityId: selectedFacility?._id,
                envId: selectedEnvironment?._id
            });
            this.setState({
                organizationIds: allowedOrganizationIds,
                selectedOrganization,
                selectedFacility,
                selectedEnvironment,
                selectedPatient,
                selectedSimulationDemo: demoEvents?.[0],
            });
        }
        catch (err) {
            this.logError('Failed to load data', err);
            ModuleFE_Toaster.toastError('Could not load data!');
        }
    };
    getCurrentlySelectedEncounter = () => {
        return this.state.openEncounters.find(encounter => encounter.patientId === this.state.currentlyOpenedTab_PatientId);
    };
    onOrganizationSelect = (organization) => {
        if (organization._id === this.state.selectedOrganization?._id)
            return;
        const allowedFacilityIds = this.getAllowedFacilityIds(organization._id);
        const allowedEnvIds = this.getAllowedEnvIds(organization._id);
        const selectedFacility = ModuleFE_Facility.cache.unique(allowedFacilityIds[0]);
        const selectedEnvironment = ModuleFE_Environment.cache.unique(allowedEnvIds[0]);
        const allowedPatients = this.getAllowedPatients(selectedEnvironment);
        storage_UserSelection.set({
            orgId: organization._id,
            facilityId: selectedFacility?._id,
            envId: selectedEnvironment?._id,
        });
        this.setState({
            selectedOrganization: organization,
            selectedFacility,
            selectedEnvironment,
            selectedPatient: allowedPatients[0],
        });
    };
    onPatientSelected = (patient) => {
        storage_LastSelectedPatient.set(patient);
        const patientId = ('fhirPatient' in patient)
            ? patient.fhirPatient
            : patient.patientId;
        // Check if this patient is already launched
        const existingEncounter = this.state.openEncounters.find(encounter => encounter.patientId === patientId);
        if (existingEncounter) {
            // Switch to existing encounter instead of launching
            this.setState({
                selectedPatient: patient,
                currentlyOpenedTab_PatientId: patientId
            });
        }
        else {
            // Just update selected patient, launch will happen via button
            this.setState({ selectedPatient: patient });
        }
    };
    closeTab = (encounter) => {
        const encounters = [...this.state.openEncounters];
        const index = encounters.findIndex(i => i.patientId === encounter.patientId);
        if (index === -1)
            return;
        encounters.splice(index, 1);
        const nextOpenEncounterPatientId = encounter.patientId === this.state.currentlyOpenedTab_PatientId
            ? undefined
            : this.state.currentlyOpenedTab_PatientId;
        this.setState({
            openEncounters: encounters,
            currentlyOpenedTab_PatientId: nextOpenEncounterPatientId,
        });
    };
    closeCurrentOpenedTab = () => {
        const currentOpenedEncounter = this.state.openEncounters.find(i => i.patientId === this.state.currentlyOpenedTab_PatientId);
        if (!currentOpenedEncounter)
            return;
        return this.closeTab(currentOpenedEncounter);
    };
    //######################### Logic - Launch #########################
    launchAssert_Environment = () => {
        const environment = this.state.selectedEnvironment;
        if (!environment) {
            ModuleFE_Toaster.toastError('Please select an Environment');
            throw new AssertionException('No Environment selected');
        }
        return environment;
    };
    launchAssert_Facility = () => {
        const facility = this.state.selectedFacility;
        if (!facility) {
            ModuleFE_Toaster.toastError('Please select an facility');
            throw new AssertionException('No facility selected');
        }
        return facility;
    };
    launchAssert_Patient = () => {
        const patient = this.state.selectedPatient;
        if (!patient) {
            ModuleFE_Toaster.toastError('Please select a patient');
            throw new AssertionException('No Patient selected');
        }
        return patient;
    };
    launchAssert_EpicPatient = () => {
        const patient = this.launchAssert_Patient();
        if (!('patientId' in patient)) {
            this.logError('Launching Epic with a non epic patient', patient);
            throw new BadImplementationException('Launching Epic with a non epic patient');
        }
        return patient;
    };
    launchAssert_DeployPatient = () => {
        const patient = this.launchAssert_Patient();
        if (!('fhirPatient' in patient)) {
            this.logError('Launching Epic with a non epic patient', patient);
            throw new BadImplementationException('Launching Epic with a non epic patient');
        }
        return patient;
    };
    launchAssert_MaxEncountersReached = () => {
        if (this.state.openEncounters.length >= 5) {
            ModuleFE_Toaster.toastError(`Cannot open more than 5 encounters simultaneously.`);
            throw new AssertionException('Max # of open encounters reached');
        }
    };
    launchImpl_Epic = async () => {
        try {
            //Assert launch
            // const ehrInstance = this.launchAssert_EHRInstance();
            const environment = this.launchAssert_Environment();
            const facility = this.launchAssert_Facility();
            const patient = this.launchAssert_EpicPatient();
            this.launchAssert_MaxEncountersReached();
            //Get SRC
            const src = await ModuleFE_Launch.getSmartOnFhirLaunchUrl(patient.patientId, environment._id, facility._id);
            //Create new demo encounter
            const demoEncounter = {
                src,
                patientId: patient.patientId,
                patientName: patient.label,
                isDeployIntegration: false,
            };
            this.reDeriveState({
                openEncounters: [...this.state.openEncounters, demoEncounter],
                message: undefined,
                currentlyOpenedTab_PatientId: patient.patientId,
            });
        }
        catch (err) {
            this.logError(err);
        }
    };
    launchImpl_Deploy = async () => {
        try {
            //Assert launch
            const { selectedEnvironment, selectedFacility } = this.state;
            const patient = this.launchAssert_DeployPatient();
            this.launchAssert_MaxEncountersReached();
            //Get SRC
            const src = await ModuleFE_Launch.getDeployIntegrationLaunchUrl(selectedEnvironment?._id, selectedFacility?._id);
            //Generate Message
            const message = deepClone(patient);
            message.parentFrameUrl = window.origin;
            //Create new demo encounter
            const demoEncounter = {
                src,
                patientId: patient.fhirPatient,
                patientName: patient.label,
                isDeployIntegration: true,
            };
            this.reDeriveState({
                openEncounters: [...this.state.openEncounters, demoEncounter],
                message: undefined,
                currentlyOpenedTab_PatientId: patient.fhirPatient,
            });
            setTimeout(() => {
                return this.messenger_setAdvisorEHRId()?.sendMessage(message);
            }, 5000);
        }
        catch (err) {
            this.logError(err);
        }
    };
    launchKmDemoSimulation = async () => {
        const demo = this.state.selectedSimulationDemo;
        if (!demo) {
            ModuleFE_Toaster.toastError('Select a demo simulation first');
            return;
        }
        try {
            const { encounterData } = await ModuleFE_SimulationDemoEvent._v1.getEncounterData({ demoEventId: demo._id }).executeSync();
            const simulation = await ModuleFE_EncounterSimulation._v1
                .createEncounterSimulation({
                encounterId: encounterData._id,
                eventId: demo.eventId,
                facilityId: encounterData.organizationMetadata.facilityId,
                environmentId: encounterData.organizationMetadata.environmentId,
                demoSimulation: true,
            }).executeSync();
            const { url } = await ModuleFE_EncounterSimulation._v1
                .getSimulationLaunchJwt({
                token: simulation.encounterId,
            })
                .executeSync();
            const tabId = `km-sim-${simulation._id}`;
            this.reDeriveState({
                openEncounters: [
                    ...this.state.openEncounters,
                    {
                        patientId: tabId,
                        patientName: demo.label,
                        src: url,
                        isDeployIntegration: false,
                    },
                ],
                currentlyOpenedTab_PatientId: tabId,
            });
        }
        catch (e) {
            ModuleFE_Toaster.toastError('Could not launch demo simulation');
        }
    };
    launchEnvironment = async () => {
        const environment = this.launchAssert_Environment();
        switch (environment.type) {
            case 'epic':
                return this.launchImpl_Epic();
            case 'deploy':
                return this.launchImpl_Deploy();
            default:
                throw new ImplementationMissingException(`Missing implementation for launch of type ${environment.type}`);
        }
    };
    //######################### Render #########################
    render() {
        return _jsxs(LL_V_L, { id: 'page-demo', children: [this.renderBackground(), this.renderTabs(), this.renderPanels()] });
    }
    //######################### Render - Page Design #########################
    renderBackground = () => {
        return _jsxs("div", { className: 'page-demo__background', children: [_jsx("div", { className: 'blob', id: 'blob-1' }), _jsx("div", { className: 'blob', id: 'blob-2' }), _jsx("div", { className: 'blob', id: 'blob-3' }), _jsx("div", { className: 'blob', id: 'blob-4' }), _jsx("div", { className: 'blob', id: 'blob-5' })] });
    };
    renderTabs = () => {
        return _jsxs(LL_H_C, { className: 'page-demo__tabs', children: [this.renderPatientTabs(), _jsx(LL_H_C, { className: 'page-demo__tabs__search', children: _jsx(ICONS.demo_tabs5.component, {}) })] });
    };
    renderPatientTabs = () => {
        const encounters = this.state.openEncounters;
        return _jsxs(_Fragment, { children: [encounters.length >= 1 && _jsxs(LL_H_C, { className: 'page-demo__tabs__tab', onClick: () => this.setState({ currentlyOpenedTab_PatientId: undefined }), children: [_jsx("div", { className: 'page-demo__tabs__tab-background' }), _jsx(ICONS.demo_patientName.component, {})] }), encounters.map(item => {
                    const className = _className('page-demo__tabs__tab', item.patientId === this.state.currentlyOpenedTab_PatientId && 'selected');
                    return _jsxs(LL_H_C, { className: className, onClick: event => this.setState({ currentlyOpenedTab_PatientId: item.patientId }), children: [_jsx("div", { className: 'page-demo__tabs__tab-background' }), _jsx(ICONS.demo_tabs2.component, {}), _jsx("div", { className: 'page-demo__tabs__tab-text', children: item.patientName }), _jsx(ICONS.x.component, { style: { width: '10px', height: '10px', marginLeft: '40px' }, onClick: event => {
                                    stopPropagation(event);
                                    this.closeTab(item);
                                } })] }, item.patientId);
                })] });
    };
    renderPanels = () => {
        return _jsxs(LL_H_C, { className: 'page-demo__panels', children: [this.renderLeftPanel(), this.renderMiddlePanel()] });
    };
    //######################### Render - Left Panel #########################
    renderLeftPanel = () => {
        const currentlySelectedEncounter = this.getCurrentlySelectedEncounter();
        return _jsxs(LL_V_L, { className: 'page-demo__panels__left', children: [_jsx(LL_H_C, { className: 'page-demo__panels__left__title', children: currentlySelectedEncounter && _jsxs(_Fragment, { children: [_jsx(ICONS.demo_patientName.component, {}), currentlySelectedEncounter.patientName] }) }), _jsxs(LL_V_L, { className: 'page-demo__panels__left__menu', children: [this.render_LeftPanel_Organization(), this.render_LeftPanel_Facility(), this.render_LeftPanel_Environment(), this.render_LeftPanel_PatientDD(), this.renderLeftPanelButtons(), this.render_LeftPanel_SimulationDemo()] })] });
    };
    render_LeftPanel_Organization = () => {
        return _jsx(TS_PropRenderer.Vertical, { label: 'Organization', children: _jsx(DropDown_Organization.selectable, { selected: this.state.selectedOrganization, queryFilter: i => this.state.organizationIds.includes(i._id), onSelected: this.onOrganizationSelect, disabled: this.state.organizationIds?.length <= 1, placeholder: undefined }) });
    };
    render_LeftPanel_Facility = () => {
        const allowedFacilityIds = this.getAllowedFacilityIds();
        return _jsx(TS_PropRenderer.Vertical, { label: 'Facilities', children: _jsx(DropDown_Facility.selectable, { queryFilter: i => allowedFacilityIds.includes(i._id), selected: this.state.selectedFacility, onSelected: selectedFacility => this.setState({ selectedFacility }), placeholder: undefined }) });
    };
    render_LeftPanel_Environment = () => {
        const allowedEnvIds = this.getAllowedEnvIds();
        return _jsx(TS_PropRenderer.Vertical, { label: 'Environments', children: _jsx(DropDown_Environment.selectable, { queryFilter: i => allowedEnvIds.includes(i._id), selected: this.state.selectedEnvironment, onSelected: selectedEnvironment => this.setState({ selectedEnvironment }), renderer: env => {
                    const ehrInstallation = ModuleFE_EHRInstallation.cache.unique(env.ehrInstallationId);
                    const organization = ModuleFE_Organization.cache.unique(ehrInstallation?.organizationId);
                    return _jsx(_Fragment, { children: `${organization?.alias} > ${ehrInstallation?.label} > ${env.label}` });
                }, placeholder: undefined }) });
    };
    render_LeftPanel_PatientDD = () => {
        const patients = this.getAllowedPatients(this.state.selectedEnvironment);
        const adapter = SimpleListAdapter(patients, patient => _jsx(_Fragment, { children: patient.item.label }));
        return _jsx(TS_PropRenderer.Vertical, { label: 'Patient', children: _jsx(TS_DropDown, { disabled: !patients.length, adapter: adapter, selected: this.state.selectedPatient, onSelected: this.onPatientSelected, placeholder: undefined }) });
    };
    render_LeftPanel_SimulationDemo = () => {
        const events = sortArray(ModuleFE_SimulationDemoEvent.cache.allMutable(), (d) => d.label.toLowerCase());
        const adapter = SimpleListAdapter(events, (event) => _jsx(_Fragment, { children: event.item.label }));
        return _jsxs(_Fragment, { children: [_jsx(TS_PropRenderer.Vertical, { label: 'Demo simulation', children: _jsx(TS_DropDown, { disabled: !events.length, adapter: adapter, selected: this.state.selectedSimulationDemo, onSelected: (selectedSimulationDemo) => this.setState({ selectedSimulationDemo }), placeholder: 'Select a demo simulation' }) }), _jsx(LL_V_L, { className: 'page-demo__left-panel-buttons', children: _jsx(Button, { variant: 'primary', disabled: !this.state.selectedSimulationDemo, onClick: () => this.launchKmDemoSimulation(), children: "Launch demo simulation" }) })] });
    };
    renderLeftPanelButtons = () => {
        return _jsx(LL_V_L, { className: 'page-demo__left-panel-buttons', children: _jsx(Button, { variant: 'primary', onClick: () => this.launchEnvironment(), children: "Launch Quai.MD Advisor" }) });
    };
    //######################### Render - Middle Panel #########################
    renderMiddlePanel = () => {
        const className = _className('page-demo__panels__middle__menu', !this.state.currentlyOpenedTab_PatientId && 'padding');
        return _jsx(LL_V_L, { className: 'page-demo__panels__middle', children: _jsxs(ResizableWrapper, { className: className, storageKeySuffix: 'advisor-demo', children: [this.renderEncounterIFrames(), this.renderMiddleMock()] }) });
    };
    renderEncounterIFrames = () => {
        return this.state.openEncounters.map(encounter => {
            return this.renderIframe(encounter);
        });
    };
    renderIframe = (encounter) => {
        if (!encounter.src)
            return;
        const focused = this.state.currentlyOpenedTab_PatientId === encounter.patientId;
        const style = focused ? {} : { display: 'none' };
        return _jsx("iframe", { onClick: e => stopPropagation(e), id: getiFrameId(encounter.patientId), src: encounter.src, className: 'match_parent', style: style, ref: focused ? this.iframeRef : undefined }, encounter.patientId);
    };
    renderMiddleMock = () => {
        const currentEncounter = this.state.openEncounters.find(encounter => encounter.patientId === this.state.currentlyOpenedTab_PatientId);
        // If we have a launch URL, show the iframe instead of the mock panel
        if (currentEncounter?.src)
            return;
        return _jsx(Demo_MockMiddlePanel, {});
    };
}
